// sharing.js - Sharing service for runs, racers, groups
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Generate a secure random token for sharing
 */
export function generateShareToken() {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let token = '';
  for (let i = 0; i < 16; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

/**
 * Create a share token for a resource
 */
export async function createShareToken({
  ownerId,
  resourceType, // 'run' | 'racer' | 'group' | 'all_racers'
  resourceId,
  permission, // 'viewer' | 'editor' | 'public'
  name = null,
  expiresAt = null
}) {
  const token = generateShareToken();
  const tokenData = {
    token,
    ownerId,
    resourceType,
    resourceId: resourceId || null,
    permission,
    name: name || `${resourceType} share`,
    createdAt: Timestamp.now(),
    expiresAt: expiresAt ? Timestamp.fromDate(expiresAt) : null,
    scope: null // Will be calculated
  };

  await setDoc(doc(db, 'shareTokens', token), tokenData);

  // Calculate and update scope
  const scope = await calculateShareScope(token, resourceType, resourceId);
  await updateDoc(doc(db, 'shareTokens', token), { scope });

  // Update resource with share info
  await updateResourceWithShare(resourceType, resourceId, token, permission);

  return token;
}

/**
 * Calculate share scope (what content is accessible through this share)
 */
export async function calculateShareScope(token, resourceType, resourceId) {
  const scope = {
    runs: [],
    racers: [],
    tracks: [],
    sessions: [],
    groups: []
  };

  if (resourceType === 'run' && resourceId) {
    const runDoc = await getDoc(doc(db, 'events', resourceId));
    if (!runDoc.exists()) return scope;

    const runData = runDoc.data();
    
    // Add the run itself
    scope.runs.push(resourceId);
    
    // Add racers in this run
    if (runData.racers && Array.isArray(runData.racers)) {
      runData.racers.forEach(r => {
        if (r.racerId && !scope.racers.includes(r.racerId)) {
          scope.racers.push(r.racerId);
        }
      });
    }
    
    // Add track
    if (runData.track && !scope.tracks.includes(runData.track)) {
      scope.tracks.push(runData.track);
    }
    
    // Add sessions
    const sessionIds = runData.sessionIds || (runData.sessionId ? [runData.sessionId] : []);
    sessionIds.forEach(s => {
      if (s && !scope.sessions.includes(s)) {
        scope.sessions.push(s);
      }
    });

    // Find other runs with same racers (bounded visibility)
    if (scope.racers.length > 0) {
      const runsRef = collection(db, 'events');
      const allRunsSnapshot = await getDocs(runsRef);
      
      allRunsSnapshot.forEach(doc => {
        const otherRun = doc.data();
        if (doc.id === resourceId) return; // Skip the original run
        
        // Check if this run has any racers in our scope
        const otherRunRacers = otherRun.racers?.map(r => r.racerId) || [];
        const hasSharedRacers = otherRunRacers.some(racerId => scope.racers.includes(racerId));
        
        if (hasSharedRacers) {
          // Check if this run is also shared with the same token (or any token from same owner)
          // For now, include it if it has shared racers (bounded scope)
          if (!scope.runs.includes(doc.id)) {
            scope.runs.push(doc.id);
          }
          
          // Add any new racers from this run
          otherRunRacers.forEach(racerId => {
            if (!scope.racers.includes(racerId)) {
              scope.racers.push(racerId);
            }
          });
        }
      });
    }
  } else if (resourceType === 'group' && resourceId) {
    // Get all racers in the group
    const racersRef = collection(db, 'timer_racers');
    const racersSnapshot = await getDocs(racersRef);
    
    racersSnapshot.forEach(doc => {
      const racer = doc.data();
      const groups = racer.groups || [];
      if (groups.includes(resourceId)) {
        scope.racers.push(doc.id);
      }
    });
    
    // Find all runs with these racers
    if (scope.racers.length > 0) {
      const runsRef = collection(db, 'events');
      const allRunsSnapshot = await getDocs(runsRef);
      
      allRunsSnapshot.forEach(doc => {
        const run = doc.data();
        const runRacers = run.racers?.map(r => r.racerId) || [];
        const hasGroupRacers = runRacers.some(racerId => scope.racers.includes(racerId));
        
        if (hasGroupRacers) {
          scope.runs.push(doc.id);
        }
      });
    }
  } else if (resourceType === 'racer' && resourceId) {
    scope.racers.push(resourceId);
    
    // Find all runs with this racer
    const runsRef = collection(db, 'events');
    const allRunsSnapshot = await getDocs(runsRef);
    
    allRunsSnapshot.forEach(doc => {
      const run = doc.data();
      const runRacers = run.racers?.map(r => r.racerId) || [];
      if (runRacers.includes(resourceId)) {
        scope.runs.push(doc.id);
      }
    });
  } else if (resourceType === 'all_racers') {
    // Get all racers owned by the user
    const racersRef = collection(db, 'timer_racers');
    const racersSnapshot = await getDocs(
      query(racersRef, where('createdBy', '==', resourceId)) // resourceId is ownerId in this case
    );
    
    racersSnapshot.forEach(doc => {
      scope.racers.push(doc.id);
    });
    
    // Find all runs with these racers
    if (scope.racers.length > 0) {
      const runsRef = collection(db, 'events');
      const allRunsSnapshot = await getDocs(runsRef);
      
      allRunsSnapshot.forEach(doc => {
        const run = doc.data();
        const runRacers = run.racers?.map(r => r.racerId) || [];
        const hasSharedRacers = runRacers.some(racerId => scope.racers.includes(racerId));
        
        if (hasSharedRacers) {
          scope.runs.push(doc.id);
        }
      });
    }
  }

  return scope;
}

/**
 * Update resource with share information
 */
async function updateResourceWithShare(resourceType, resourceId, token, permission) {
  if (resourceType === 'run' && resourceId) {
    const updates = {
      shareTokens: arrayUnion(token)
    };
    
    if (permission === 'public') {
      updates.isPublic = true;
    }
    
    await updateDoc(doc(db, 'events', resourceId), updates);
  } else if (resourceType === 'racer' && resourceId) {
    await updateDoc(doc(db, 'timer_racers', resourceId), {
      shareTokens: arrayUnion(token)
    });
  } else if (resourceType === 'group' && resourceId) {
    // Groups are stored as part of racers, so we update all racers in the group
    const racersRef = collection(db, 'timer_racers');
    const racersSnapshot = await getDocs(racersRef);
    
    const updatePromises = [];
    racersSnapshot.forEach(docSnap => {
      const racer = docSnap.data();
      const groups = racer.groups || [];
      if (groups.includes(resourceId)) {
        updatePromises.push(
          updateDoc(doc(db, 'timer_racers', docSnap.id), {
            shareTokens: arrayUnion(token),
            sharedGroups: arrayUnion(resourceId)
          })
        );
      }
    });
    
    await Promise.all(updatePromises);
  }
}

/**
 * Get share token data
 */
export async function getShareToken(token) {
  const tokenDoc = await getDoc(doc(db, 'shareTokens', token));
  if (!tokenDoc.exists()) return null;
  
  return {
    id: tokenDoc.id,
    ...tokenDoc.data()
  };
}

/**
 * Check if resource is accessible through share token
 */
export async function isAccessibleThroughShare(resourceId, resourceType, token) {
  const tokenData = await getShareToken(token);
  if (!tokenData) return false;
  
  // Check if expired
  if (tokenData.expiresAt && tokenData.expiresAt.toDate() < new Date()) {
    return false;
  }
  
  const scope = tokenData.scope || {};
  
  // Direct match
  if (tokenData.resourceId === resourceId && tokenData.resourceType === resourceType) {
    return true;
  }
  
  // Dependency match (within scope)
  switch (resourceType) {
    case 'racer':
      return scope.racers && scope.racers.includes(resourceId);
    case 'run':
      return scope.runs && scope.runs.includes(resourceId);
    case 'track':
      return scope.tracks && scope.tracks.includes(resourceId);
    case 'group':
      return scope.groups && scope.groups.includes(resourceId);
  }
  
  return false;
}

/**
 * Get share URL for a token
 */
export function getShareUrl(token, resourceType = null, resourceId = null) {
  if (resourceType === 'run' && resourceId) {
    return `/shared/run/${resourceId}?token=${token}`;
  }
  return `/shared/${token}`;
}

/**
 * Get public URL for a run
 */
export function getPublicRunUrl(runId) {
  return `/public/run/${runId}`;
}

/**
 * Revoke a share token
 */
export async function revokeShareToken(token) {
  const tokenData = await getShareToken(token);
  if (!tokenData) return;
  
  // Remove token from resources
  if (tokenData.resourceType === 'run' && tokenData.resourceId) {
    await updateDoc(doc(db, 'events', tokenData.resourceId), {
      shareTokens: arrayRemove(token),
      isPublic: false // Remove public status if it was public
    });
  }
  
  // Delete token
  await updateDoc(doc(db, 'shareTokens', token), {
    revoked: true,
    revokedAt: Timestamp.now()
  });
}

/**
 * Get all share tokens for a user
 */
export async function getUserShareTokens(userId) {
  const tokensRef = collection(db, 'shareTokens');
  const tokensSnapshot = await getDocs(
    query(tokensRef, where('ownerId', '==', userId))
  );
  
  return tokensSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })).filter(token => !token.revoked);
}
